import type { GridColumnsInitialState } from "@mui/x-data-grid-pro";

import { AssetStatusEnum } from "@/apis/services/AssetService";
import type {
  CreateSettingsResponse,
  RiskRatingOption,
  RiskRatingSettingOutput,
} from "@/apis/services/OrganizationService";
import {
  InstanceViewEnum,
  SettingNameEnum,
  type BaseDataGridSettingInput,
  type ViewModelInput,
} from "@/apis/services/OrganizationService";
import { COLUMNS_KEYS_TO_IGNORE } from "@/constants/grid.constants";
import {
  DEFAULT_DATAGRID_COLUMN,
  DEFAULT_DATAGRID_FILTER,
} from "@/constants/settings.constants";

export const getOrgSettings = (
  views: Record<string, ViewModelInput>,
  page: string,
  settings: string
) => {
  if (!views || !views[page] || !views[page]["settings"][settings])
    return undefined;
  return views[page]["settings"][settings];
};

export const getFilterSetting = (
  id: string,
  settings: BaseDataGridSettingInput
): string[] | number[] | any | null | undefined => {
  const filter = settings.filters.filter((s) => s.id === id)[0];
  if (!filter) return;
  if ((filter.value as any)?.length && (filter.value as any)[0] === "all")
    return;

  return filter.value ?? filter.default_value;
};

export const getSettingsUpdateValues = (
  filters: {
    selectedHazards: string[];
    selectedConsequences: string[];
    climateScenario: string;
    timeHorizon: number | string;
    filterStatus: AssetStatusEnum[];
    filterNotAssessed: boolean;
  },
  visibleColumns: string[],
  groupId?: string
) => {
  // TODO make this more readable
  const _columns = [
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "checkbox_selection",
      label: "checkbox_selection",
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "action",
      label: "action",
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "name",
      label: "name",
      visible: visibleColumns.includes("name") ?? false,
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "nickname",
      label: "nickname",
      visible: visibleColumns.includes("nickname") ?? false,
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "country",
      label: "country",
      visible: visibleColumns.includes("country") ?? false,
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "street_address",
      label: "street_address",
      visible: visibleColumns.includes("street_address"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "primary_use",
      label: "primary_use",
      visible: visibleColumns.includes("primary_use"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "ownership",
      label: "ownership",
      visible: visibleColumns.includes("ownership"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "business_group",
      label: "business_group",
      visible: visibleColumns.includes("business_group"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "status",
      label: "status",
      visible: visibleColumns.includes("status"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "city",
      label: "city",
      visible: visibleColumns.includes("city"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "gross_asset_value",
      label: "gross_asset_value",
      visible: visibleColumns.includes("gross_asset_value"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "replacement_cost",
      label: "replacement_cost",
      visible: visibleColumns.includes("replacement_cost"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "year_of_construction",
      label: "year_of_construction",
      visible: visibleColumns.includes("year_of_construction"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "occ_area",
      label: "occ_area",
      visible: visibleColumns.includes("occ_area"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "n_floors",
      label: "n_floors",
      visible: visibleColumns.includes("n_floors"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "total_building_population",
      label: "total_building_population",
      visible: visibleColumns.includes("total_building_population"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "latitude",
      label: "latitude",
      visible: visibleColumns.includes("latitude"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "longitude",
      label: "longitude",
      visible: visibleColumns.includes("longitude"),
    },
    {
      ...DEFAULT_DATAGRID_COLUMN,
      id: "footprint_geometry",
      label: "footprint_geometry",
      visible: visibleColumns.includes("footprint_geometry"),
    },
  ];
  const _filters = [
    {
      ...DEFAULT_DATAGRID_FILTER,
      id: "asset_status",
      value: filters.filterStatus,
      field_type: "MULTISELECT",
      label: "status",
      default_value: [AssetStatusEnum.InUse],
    },
    {
      ...DEFAULT_DATAGRID_FILTER,
      id: "hazards",
      value: filters.selectedHazards,
      field_type: "MULTISELECT",
      label: "hazards",
      default_value: ["all_hazards"],
    },
    {
      ...DEFAULT_DATAGRID_FILTER,
      id: "consequences",
      value: filters.selectedConsequences,
      field_type: "MULTISELECT",
      label: "consequences",
    },
    {
      ...DEFAULT_DATAGRID_FILTER,
      id: "time_horizons",
      value: filters.timeHorizon === "all" ? null : [filters.timeHorizon],
      field_type: "MULTISELECT",
      label: "time_horizons",
      default_value: null,
    },
    {
      ...DEFAULT_DATAGRID_FILTER,
      id: "climate_scenarios",
      value:
        filters.climateScenario === "all" ? null : [filters.climateScenario],
      field_type: "MULTISELECT",
      label: "climate_scenarios",
      default_value: null,
    },
  ];

  return !groupId
    ? {
        views: {
          portfolio: {
            settings: {
              asset_data_grid: {
                columns: _columns,
                filters: _filters,
                general: {
                  exclude_not_assessed: filters.filterNotAssessed,
                },
              },
            },
          },
        },
      }
    : {
        view: InstanceViewEnum.Group,
        component: SettingNameEnum.AssetDataGrid,
        view_id: groupId,
        params: {
          columns: _columns,
          filters: _filters,
          general: {
            exclude_not_assessed: filters.filterNotAssessed,
          },
        },
      };
};

export const getColumns = (dataGridSettings?: BaseDataGridSettingInput) => {
  const columns = ["status", "gross_asset_value", "latitude", "longitude"];
  columns.push("footprint_geometry");
  if (!dataGridSettings) return columns;

  const dataGridColumns = dataGridSettings.columns
    .filter((c) => c.visible === true && !COLUMNS_KEYS_TO_IGNORE.includes(c.id))
    .map((n) => n.id);

  return dataGridColumns.concat(columns);
};

export const getDefaultColumns = (
  settings: BaseDataGridSettingInput
): GridColumnsInitialState => {
  const columns: any = {};

  settings.columns.forEach((col) => {
    columns[col.id] = col.visible;
  });

  const model = {
    columnVisibilityModel: columns,
  };

  return model;
};

export const getDataGridSettings = (
  type: "portfolio" | "group",
  orgSettings?: CreateSettingsResponse,
  groupSettings?: CreateSettingsResponse
) => {
  if (!orgSettings) return;

  if (type === "portfolio") {
    const settings = orgSettings.views.portfolio.settings
      .asset_data_grid as BaseDataGridSettingInput;
    return settings;
  }

  const groupDataGridSettings = groupSettings?.views.group.settings
    .asset_data_grid as BaseDataGridSettingInput;
  if (!groupDataGridSettings) {
    // return the default group settings from an organizational level
    const orgDataGridSettings = orgSettings.views.group.settings
      .asset_data_grid as BaseDataGridSettingInput;
    return orgDataGridSettings;
  }
  return groupDataGridSettings;
};

type Cache = {
  keys: () => IterableIterator<string>;
  delete: (key: string) => void;
};

export const clearCachePartially = (cache: Cache, excludedKeys: string[]) => {
  const cacheKeys: string[] = Array.from(cache.keys());

  cacheKeys.map((key) => {
    if (excludedKeys.includes(key)) return;
    cache.delete(key);
  });
};

export const getOrgSettingsExcludedCacheKeys = (orgId: string) => [
  "/api/me",
  "useAllOrganizations",
  `@"useGetOrgSettings","${orgId}",`,
  `@"getOrganizationFeatureFlags","${orgId}",`,
];

export interface RiskRatingSettings {
  [key: number]: RiskRatingOption;
}

export const getRiskRatingSettings = (settings: CreateSettingsResponse) => {
  // Returns the configured risk ratings for the organization
  const orgSettings = settings?.views?.organization?.settings;
  const ratings = orgSettings?.risk_ratings as RiskRatingSettingOutput;
  if (!ratings || !orgSettings) return {};

  const options = ratings.options ?? [];
  const data = options.reduce((prev: RiskRatingSettings, cur) => {
    prev[cur.risk_score] = cur;
    return prev;
  }, {});

  return data;
};
